import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQzTray } from '../context/QzTrayContext';
import { useDataCache } from '../context/DataCacheContext';
import Modal from '../components/Modal';
import MarkUsedModal from '../components/MarkUsedModal';
import './StockHousePage.css';

const VIEW_MODE_KEY = 'stockHouseViewMode';
const VIEW_TAB_KEY = 'stockHouseViewTab';

const formatCurrency = (value, currency = 'INR') => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '—';
  const formatted = num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const curUpper = String(currency).trim().toUpperCase();
  if (curUpper === 'USD') return `$${formatted}`;
  if (curUpper === 'EUR') return `€${formatted}`;
  if (curUpper === 'GBP') return `£${formatted}`;
  return `₹${formatted}`;
};

const pickBestPiece = (pieces, needed) => {
  if (!Array.isArray(pieces) || pieces.length === 0) return { recommended: null, lowRemainder: false };
  const sorted = [...pieces].sort((a, b) => toNumber(a.length) - toNumber(b.length));
  const exactMatch = sorted.find(p => Math.abs(toNumber(p.length) - needed) < 0.01);
  if (exactMatch) return { recommended: exactMatch, lowRemainder: false };
  const usefulMatch = sorted.find(p => toNumber(p.length) - needed >= 2.0);
  if (usefulMatch) return { recommended: usefulMatch, lowRemainder: false };
  return { recommended: sorted[0], lowRemainder: true };
};


const selectLineImage = (lineItem) =>
  lineItem?.image_url ||
  lineItem?.imageUrl ||
  lineItem?.image ||
  lineItem?.variant_image ||
  lineItem?.product_image ||
  null;

const formatQuantity = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return value || '—';
  if (Number.isInteger(num)) return num;
  return num.toFixed(2);
};

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value.toDate === 'function') return value.toDate().toLocaleDateString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_) {
    return {};
  }
};

const toNumber = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizeSku = (sku) => (sku ? String(sku).trim().toUpperCase() : '');

const getV9LineCandidateKey = (orderId, sku) =>
  `${String(orderId || '').trim()}::${normalizeSku(sku)}`;


function StockHousePage() {
  const navigate = useNavigate();
  const { currentUser, canAccessPage, canPerformAction } = useAuth();
  const { printSticker } = useQzTray() || {};
  const dataCache = useDataCache();
  const piecesCacheKey = 'inventoryPieces:/api/v1/inventory/all-pieces';

  const invalidateInventoryCaches = useCallback(() => {
    dataCache.invalidate(piecesCacheKey);
    dataCache.invalidatePrefix('inventorySummary:');
    dataCache.invalidatePrefix('inventorySummarySearch:');
    dataCache.invalidatePrefix('inventoryLedger:');
  }, [dataCache, piecesCacheKey]);
  const [error, setError] = useState('');
  const [pieces, setPieces] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid-2';
    try {
      return localStorage.getItem(VIEW_MODE_KEY) || 'grid-2';
    } catch (err) {
      console.warn('Unable to read stock house view mode from storage:', err);
      return 'grid-2';
    }
  });
  const [viewTab, setViewTab] = useState(() => {
    if (typeof window === 'undefined') return 'inStockV9';
    try {
      const stored = localStorage.getItem(VIEW_TAB_KEY);
      if (stored === 'readyNew' || stored === 'inStockV9') return stored;
      if (stored === 'ready' || stored === 'pool') return 'readyNew';
      return 'inStockV9';
    } catch (err) {
      console.warn('Unable to read stock house view tab from storage:', err);
      return 'inStockV9';
    }
  }); // 'inStockV9' | 'readyNew'
  const [searchTerm, setSearchTerm] = useState('');
  const [readyItemCountFilter, setReadyItemCountFilter] = useState('all'); // 'all' | number-as-string
  const [readyBatchFilter, setReadyBatchFilter] = useState([]); // array of batchIds (uppercased)
  const [readySortKey, setReadySortKey] = useState('orderNumber'); // 'orderNumber' | 'sku' | 'batch' | 'itemCount'
  const [readySortOrder, setReadySortOrder] = useState('desc'); // 'asc' | 'desc'
  const [readyV9Orders, setReadyV9Orders] = useState([]);
  const [isLoadingReadyV9, setIsLoadingReadyV9] = useState(false);
  const [isMarkingPackedV9, setIsMarkingPackedV9] = useState(false);
  const [inStockV9Cards, setInStockV9Cards] = useState([]);
  const [isLoadingInStockV9, setIsLoadingInStockV9] = useState(false);
  const isRefreshing = isLoadingReadyV9 || isLoadingInStockV9;
  const [v9LineCandidatesByKey, setV9LineCandidatesByKey] = useState({});
  const [v9LineCandidatesLoading, setV9LineCandidatesLoading] = useState(false);
  const readyBatchDetailsRef = useRef(null);

  const canMarkUsedAction = canPerformAction('orders.markUsed');

  const [usePieceTarget, setUsePieceTarget] = useState(null); // { sku, pieceId, length }
  const [usePieceCandidates, setUsePieceCandidates] = useState([]); // [{ invKey, orderDocId, orderNumber, qtyNumber, quantity }]
  const [usePieceSelectedInvKey, setUsePieceSelectedInvKey] = useState('');
  const [usePieceError, setUsePieceError] = useState('');
  const [isUsingPiece, setIsUsingPiece] = useState(false);
  const [v9MarkModalOpen, setV9MarkModalOpen] = useState(false);
  const [v9MarkModalRow, setV9MarkModalRow] = useState(null);
  const [v9MarkCandidates, setV9MarkCandidates] = useState(null);
  const [v9MarkLoading, setV9MarkLoading] = useState(false);
  const [v9MarkSubmitting, setV9MarkSubmitting] = useState(false);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const el = readyBatchDetailsRef.current;
      if (!el || !el.open) return;
      if (el.contains(event.target)) return;
      el.open = false;
    };

    const handleKeyDown = (event) => {
      const el = readyBatchDetailsRef.current;
      if (!el || !el.open) return;
      if (event.key === 'Escape') {
        el.open = false;
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const authedFetch = useCallback(async (url, init = {}) => {
    if (!currentUser) throw new Error('You must be logged in.');
    const token = await currentUser.getIdToken();
    const headers = {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    };
    if (init.body != null && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, { ...init, headers });
  }, [currentUser]);

  const fetchReadyV9Orders = useCallback(async () => {
    if (!currentUser) return;
    setError('');
    setIsLoadingReadyV9(true);
    try {
      const listed = [];
      let cursor = '';
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams();
        params.set('status', 'READY_TO_PACK');
        params.set('limit', '200');
        if (cursor) params.set('cursor', cursor);
        const listRes = await authedFetch(`/api/v9/orders?${params.toString()}`);
        const listData = await parseJsonSafe(listRes);
        if (!listRes.ok || !listData?.ok) {
          throw new Error(listData?.error?.message || 'Failed to load V9 ready orders.');
        }
        const pageItems = Array.isArray(listData?.result?.items) ? listData.result.items : [];
        listed.push(...pageItems);
        hasMore = listData?.result?.hasMore === true;
        cursor = String(listData?.result?.nextCursor || '').trim();
        if (!hasMore || !cursor) break;
      }

      const detailedCards = [];
      const chunkSize = 8;
      for (let i = 0; i < listed.length; i += chunkSize) {
        const chunk = listed.slice(i, i + chunkSize);
        // eslint-disable-next-line no-await-in-loop
        const chunkCards = await Promise.all(chunk.map(async (row) => {
          const rowId = String(row?.id || '').trim();
          if (!rowId) return null;
          let detailOrder = row;
          let lines = Array.isArray(row?.lines) ? row.lines : [];
          let lineActivityByLine = row?.lineActivityByLine && typeof row.lineActivityByLine === 'object' ? row.lineActivityByLine : null;
          let hasDetailsInline = lines.length > 0 && lineActivityByLine !== null;

          if (!hasDetailsInline) {
            try {
              const detailRes = await authedFetch(`/api/v9/orders/${encodeURIComponent(rowId)}`);
              const detailData = await parseJsonSafe(detailRes);
              if (detailRes.ok && detailData?.ok) {
                detailOrder = detailData?.result?.order || row;
                lines = Array.isArray(detailData?.result?.lines) ? detailData.result.lines : [];
                lineActivityByLine =
                  detailData?.result?.lineActivityByLine && typeof detailData.result.lineActivityByLine === 'object'
                    ? detailData.result.lineActivityByLine
                    : {};
                hasDetailsInline = true;
              }
            } catch (_) {
              // Keep list row fallback if details fail for a row.
            }
          }

          const lineRows = lines
            .map((line, idx) => {
              const sku = String(line?.sku || '').trim().toUpperCase();
              const qty = toNumber(line?.netRequiredQty);
              if (!sku || qty <= 0) return null;
              const lineId = String(line?.id || `${rowId}_${idx}`);
              const activity = hasDetailsInline && lineActivityByLine?.[lineId] && typeof lineActivityByLine[lineId] === 'object'
                ? lineActivityByLine[lineId]
                : null;
              const receivedBatchIds = Array.isArray(activity?.receivedBatchIds)
                ? activity.receivedBatchIds.map((b) => String(b || '').trim().toUpperCase()).filter(Boolean)
                : [];
              const awaitingBatchIds = Array.isArray(activity?.awaitingBatchIds)
                ? activity.awaitingBatchIds.map((b) => String(b || '').trim().toUpperCase()).filter(Boolean)
                : [];
              const mergedBatchIds = Array.from(new Set([...receivedBatchIds, ...awaitingBatchIds]));
              const usedPieceIds = Array.isArray(activity?.usedPieceIds) ? activity.usedPieceIds : [];
              return {
                id: lineId,
                sku,
                qty,
                title: String(line?.productName || sku),
                imageUrl: selectLineImage(line) || line?.productImageUrl || null,
                used: usedPieceIds.length > 0,
                usedPieceIds,
                batchReceived: receivedBatchIds.length > 0,
                batchId: mergedBatchIds.length === 1 ? mergedBatchIds[0] : null,
                batchIds: mergedBatchIds,
              };
            })
            .filter(Boolean);

          const orderNumber = String(
            detailOrder?.sourceLinks?.shopify?.orderName ||
            detailOrder?.sourceLinks?.zoho?.referenceNumber ||
            detailOrder?.externalOrderId ||
            detailOrder?.id ||
            rowId
          ).trim();
          const customer = String(
            detailOrder?.customer?.name ||
            detailOrder?.customerName ||
            detailOrder?.shippingAddress?.name ||
            detailOrder?.billingAddress?.name ||
            'Customer'
          ).trim();
          const total = toNumber(detailOrder?.amounts?.netCollectible || detailOrder?.amounts?.orderAmount);
          const paymentType = String(detailOrder?.paymentMode || '').trim();
          const source = String(detailOrder?.source || '').trim();
          const shipment = detailOrder?.shipment && typeof detailOrder.shipment === 'object' ? detailOrder.shipment : {};
          return {
            id: rowId,
            orderDocId: rowId,
            orderNumber,
            orderDate: formatDate(detailOrder?.createdAtSource || detailOrder?.createdAt || detailOrder?.updatedAt),
            customer,
            totalDisplay: formatCurrency(total, 'INR'),
            paymentType,
            source,
            imageUrl: lineRows[0]?.imageUrl || null,
            statusText: 'Ready to pack',
            lineCount: lineRows.length,
            totalQty: lineRows.reduce((sum, li) => sum + toNumber(li.qty), 0),
            lines: lineRows,
            packLineItems: lineRows.map((line) => ({ sku: line.sku, qty: toNumber(line.qty) })),
            prefillPartner: String(shipment?.partner || '').trim().toLowerCase(),
            prefillAwb: String(shipment?.prefillAwb || shipment?.awb || '').trim(),
          };
        }));
        detailedCards.push(...chunkCards.filter(Boolean));
      }

      setReadyV9Orders(detailedCards);
    } catch (err) {
      setReadyV9Orders([]);
      setError(err?.message || 'Failed to load V9 ready orders.');
    } finally {
      setIsLoadingReadyV9(false);
    }
  }, [authedFetch, currentUser]);


  const fetchInStockV9Orders = useCallback(async () => {
    if (!currentUser) return;
    setIsLoadingInStockV9(true);
    setError('');
    try {
      // Fetch all MARK_USED_ELIGIBLE open action-queue items — already filtered by V9 backend.
      const allItems = [];
      let cursor = '';
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams();
        params.set('actionType', 'MARK_USED_ELIGIBLE');
        params.set('status', 'OPEN');
        params.set('limit', '200');
        if (cursor) params.set('cursor', cursor);
        // eslint-disable-next-line no-await-in-loop
        const res = await authedFetch(`/api/v9/action-queue?${params.toString()}`);
        // eslint-disable-next-line no-await-in-loop
        const data = await parseJsonSafe(res);
        if (!res.ok || !data?.ok) throw new Error(data?.error?.message || 'Failed to load in-stock queue.');
        const pageItems = Array.isArray(data?.result?.items) ? data.result.items : [];
        allItems.push(...pageItems);
        hasMore = data?.result?.hasMore === true;
        cursor = String(data?.result?.nextCursor || '').trim();
        if (!hasMore || !cursor) break;
      }

      // Group by orderId → one card per order, each card has its eligible lines.
      const cardMap = new Map();
      allItems.forEach((item) => {
        const orderId = String(item?.orderId || '').trim();
        const sku = String(item?.sku || '').trim().toUpperCase();
        const pendingQty = Number(item?.pendingQty || 0);
        if (!orderId || !sku || pendingQty <= 0) return;
        if (!cardMap.has(orderId)) {
          cardMap.set(orderId, {
            id: orderId,
            orderNumber: String(item?.orderNumber || orderId),
            orderStatus: String(item?.orderStatus || ''),
            inStockLines: [],
          });
        }
        cardMap.get(orderId).inStockLines.push({
          sku,
          qty: pendingQty,
          title: String(item?.productName || sku),
          imageUrl: String(item?.productImageUrl || '').trim() || null,
          allocationMode: String(item?.meta?.allocationMode || '').trim().toUpperCase(),
          componentSummary: Array.isArray(item?.meta?.componentSummary) ? item.meta.componentSummary : [],
          stockSkuOptions: Array.isArray(item?.meta?.stockSkuOptions) ? item.meta.stockSkuOptions : [],
        });
      });

      setInStockV9Cards(Array.from(cardMap.values()));
    } catch (err) {
      setInStockV9Cards([]);
      setV9LineCandidatesByKey({});
      setError(err?.message || 'Failed to load V9 in-stock orders.');
    } finally {
      setIsLoadingInStockV9(false);
    }
  }, [authedFetch, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewTab !== 'inStockV9' || !currentUser || inStockV9Cards.length === 0) {
      setV9LineCandidatesByKey({});
      setV9LineCandidatesLoading(false);
      return undefined;
    }

    const lines = [];
    const seen = new Set();
    inStockV9Cards.forEach((card) => {
      (card.inStockLines || []).forEach((line) => {
        const orderId = String(card?.id || '').trim();
        const sku = normalizeSku(line?.sku);
        const key = getV9LineCandidateKey(orderId, sku);
        if (!orderId || !sku || seen.has(key)) return;
        seen.add(key);
        lines.push({ orderId, sku, key });
      });
    });

    if (lines.length === 0) {
      setV9LineCandidatesByKey({});
      setV9LineCandidatesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setV9LineCandidatesLoading(true);

    const loadCandidates = async () => {
      const next = {};
      const concurrency = 6;
      for (let index = 0; index < lines.length; index += concurrency) {
        const chunk = lines.slice(index, index + concurrency);
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.all(chunk.map(async ({ orderId, sku, key }) => {
          try {
            const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(sku)}/stock-candidates`);
            const data = await parseJsonSafe(res);
            if (!res.ok || !data?.ok) return { key, value: null };
            return { key, value: data.result || null };
          } catch (_) {
            return { key, value: null };
          }
        }));
        if (cancelled) return;
        results.forEach(({ key, value }) => {
          if (value) next[key] = value;
        });
        setV9LineCandidatesByKey((prev) => ({ ...prev, ...next }));
      }
      if (!cancelled) setV9LineCandidatesLoading(false);
    };

    setV9LineCandidatesByKey({});
    loadCandidates().catch(() => {
      if (!cancelled) setV9LineCandidatesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [authedFetch, currentUser, inStockV9Cards, viewTab]);

  const fetchPieces = async () => {
    if (!currentUser || !(canAccessPage('inventory') || canAccessPage('stockHouse'))) return;
    try {
      const cachedEntry = dataCache.getCacheEntry(piecesCacheKey);
      const cachedPieces = Array.isArray(cachedEntry?.value?.pieces) ? cachedEntry.value.pieces : [];
      if (cachedPieces.length > 0) {
        setPieces(cachedPieces);
      }
      if (cachedEntry?.value && !cachedEntry.stale) {
        return;
      }
      const { data } = await dataCache.getInventoryPieces();
      setPieces(Array.isArray(data?.pieces) ? data.pieces : []);
    } catch (err) {
      console.warn('Failed to load pieces:', err?.message || err);
    }
  };

  useEffect(() => {
    if (canAccessPage('inventory') || canAccessPage('stockHouse')) {
      fetchPieces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, canAccessPage]);

  useEffect(() => {
    if (viewTab !== 'readyNew') return;
    fetchReadyV9Orders();
  }, [viewTab, fetchReadyV9Orders]);

  useEffect(() => {
    if (viewTab !== 'inStockV9') return;
    fetchInStockV9Orders();
  }, [viewTab, fetchInStockV9Orders]);

  const refreshAll = async () => {
    if (viewTab === 'readyNew') {
      await fetchReadyV9Orders();
    }
    if (viewTab === 'inStockV9') {
      await fetchInStockV9Orders();
    }
    if (canAccessPage('inventory') || canAccessPage('stockHouse')) {
      await fetchPieces();
    }
  };

  const markPackedForV9Order = async (item) => {
    if (!currentUser) return;
    const orderId = String(item?.orderDocId || item?.id || '').trim();
    if (!orderId) return;

    const defaultPartner = String(item?.prefillPartner || 'delhivery').trim().toLowerCase() || 'delhivery';
    const partnerInput = window.prompt('Shipment partner (delhivery / shiprocket):', defaultPartner);
    if (partnerInput === null) return;
    const shipmentPartner = String(partnerInput || '').trim().toLowerCase();
    if (!shipmentPartner) {
      setError('Shipment partner is required.');
      return;
    }

    const awbInput = window.prompt('AWB number:', String(item?.prefillAwb || '').trim());
    if (awbInput === null) return;
    const awb = String(awbInput || '').trim();
    if (!awb) {
      setError('AWB is required to mark packed.');
      return;
    }

    setIsMarkingPackedV9(true);
    setError('');
    try {
      let lineItems = Array.isArray(item?.packLineItems) ? item.packLineItems : [];
      lineItems = lineItems
        .map((li) => ({ sku: String(li?.sku || '').trim().toUpperCase(), qty: toNumber(li?.qty) }))
        .filter((li) => li.sku && li.qty > 0);

      if (lineItems.length === 0) {
        const detailRes = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}`);
        const detailData = await parseJsonSafe(detailRes);
        if (!detailRes.ok || !detailData?.ok) {
          throw new Error(detailData?.error?.message || 'Failed to load order lines for packing.');
        }
        const lines = Array.isArray(detailData?.result?.lines) ? detailData.result.lines : [];
        lineItems = lines
          .map((line) => ({ sku: String(line?.sku || '').trim().toUpperCase(), qty: toNumber(line?.netRequiredQty) }))
          .filter((li) => li.sku && li.qty > 0);
      }
      if (lineItems.length === 0) {
        throw new Error('No packable line items found for this order.');
      }

      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/pack`, {
        method: 'POST',
        body: JSON.stringify({
          shipmentPartner,
          awb,
          packages: [{
            packageRef: `PKG-${Date.now()}`,
            lineItems,
          }],
        }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error?.message || 'Failed to mark packed.');
      }
      await fetchReadyV9Orders();
    } catch (e) {
      setError(e?.message || 'Failed to mark packed.');
    } finally {
      setIsMarkingPackedV9(false);
    }
  };

  const closeV9MarkUsedModal = useCallback(() => {
    if (v9MarkSubmitting) return;
    setV9MarkModalOpen(false);
    setV9MarkModalRow(null);
    setV9MarkCandidates(null);
    setV9MarkLoading(false);
    setV9MarkSubmitting(false);
  }, [v9MarkSubmitting]);

  const openV9MarkUsedModal = useCallback(async (row) => {
    if (!canMarkUsedAction) return;
    const orderId = String(row?.orderDocId || row?.orderId || '').trim();
    const sku = String(row?.sku || '').trim().toUpperCase();
    if (!orderId || !sku) return;
    setV9MarkModalRow({ ...row, orderDocId: orderId, sku });
    setV9MarkCandidates(null);
    setV9MarkModalOpen(true);
    setV9MarkLoading(true);
    setError('');
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(sku)}/stock-candidates`);
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error?.message || 'Failed to load V9 stock candidates.');
      }
      setV9MarkCandidates(data.result || null);
    } catch (err) {
      setError(err?.message || 'Failed to load V9 stock candidates.');
      setV9MarkCandidates(null);
    } finally {
      setV9MarkLoading(false);
    }
  }, [authedFetch, canMarkUsedAction]);

  const submitV9MarkUsed = useCallback(async (consume) => {
    const orderId = String(v9MarkModalRow?.orderDocId || v9MarkModalRow?.orderId || '').trim();
    const sku = String(v9MarkModalRow?.sku || '').trim().toUpperCase();
    if (!orderId || !sku) return;
    setV9MarkSubmitting(true);
    setError('');
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/mark-used`, {
        method: 'POST',
        body: JSON.stringify({
          items: [{ sku, consume }],
        }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error?.message || 'Failed to mark used.');
      }
      const jobs = data?.result?.printJobs || [];
      for (const job of jobs) { printSticker?.(job.payload)?.catch?.(() => {}); }
      invalidateInventoryCaches();
      setV9MarkModalOpen(false);
      setV9MarkModalRow(null);
      setV9MarkCandidates(null);
      await fetchInStockV9Orders();
    } catch (err) {
      setError(err?.message || 'Failed to mark used.');
    } finally {
      setV9MarkSubmitting(false);
    }
  }, [authedFetch, fetchInStockV9Orders, v9MarkModalRow, invalidateInventoryCaches, printSticker]);

  const closeUsePieceModal = () => {
    if (isUsingPiece) return;
    setUsePieceTarget(null);
    setUsePieceCandidates([]);
    setUsePieceSelectedInvKey('');
    setUsePieceError('');
  };

  const openUsePieceModal = (group, piece) => {
    if (!canMarkUsedAction) return;
    const sku = group?.sku;
    const pieceId = piece?.pieceId || piece?.id;
    const length = toNumber(piece?.length);
    if (!sku || !pieceId) return;

    const candidates = (group?.orders || []).filter((o) => length >= toNumber(o.qtyNumber ?? o.quantity));
    if (candidates.length === 0) {
      setUsePieceTarget({ sku, pieceId, length });
      setUsePieceCandidates([]);
      setUsePieceSelectedInvKey('');
      setUsePieceError('No eligible order lines found for this piece.');
      return;
    }

    setUsePieceTarget({ sku, pieceId, length });
    setUsePieceCandidates(candidates);
    setUsePieceSelectedInvKey(candidates.length === 1 ? (candidates[0].invKey || candidates[0].orderDocId || '') : '');
    setUsePieceError('');
  };

  const submitUsePiece = async () => {
    if (!currentUser || !canMarkUsedAction || !usePieceTarget) return;
    const selected =
      usePieceCandidates.find((c) => (c.invKey || c.orderDocId) === usePieceSelectedInvKey) ||
      usePieceCandidates[0];
    if (!selected?.orderDocId || (!selected?.invKey && !selected?.isV9)) {
      setUsePieceError('Please select an order to assign this piece to.');
      return;
    }

    setIsUsingPiece(true);
    setUsePieceError('');
    try {

      // ── V9 path ──────────────────────────────────────────────────────────────
      if (selected.isV9) {
        const sku = normalizeSku(selected.sku || usePieceTarget.sku);
        const qty = toNumber(selected.qtyNumber);
        const response = await authedFetch(
          `/api/v9/orders/${encodeURIComponent(selected.orderDocId)}/mark-used`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  sku,
                  requestedQty: qty,
                  consume: [{ stockPieceId: usePieceTarget.pieceId, qty }],
                },
              ],
            }),
          }
        );
        const data = await parseJsonSafe(response);
        if (!response.ok || !data?.ok) {
          throw new Error(data?.error?.message || 'Failed to mark used.');
        }
        const jobs = data?.result?.printJobs || [];
        for (const job of jobs) { printSticker?.(job.payload)?.catch?.(() => {}); }

        invalidateInventoryCaches();
        closeUsePieceModal();
        // Refresh pieces (for the modal) and re-fetch in-stock queue from action queue.
        try {
          const { data: freshPiecesData } = await dataCache.getInventoryPieces({ force: true });
          const freshPieces = Array.isArray(freshPiecesData?.pieces) ? freshPiecesData.pieces : pieces;
          setPieces(freshPieces);
        } catch (_) { /* non-fatal */ }
        await fetchInStockV9Orders();
        return;
      }

      throw new Error('Unsupported legacy order path.');
    } catch (err) {
      setUsePieceError(err.message);
    } finally {
      setIsUsingPiece(false);
    }
  };


  // SKU groups for V9 in-stock tab — built from inStockV9Cards + piecesBySku.
  const skuGroupsV9 = useMemo(() => {
    const map = new Map();
    inStockV9Cards.forEach((card) => {
      (card.inStockLines || []).forEach((line) => {
        const sku = String(line.sku || '').trim().toUpperCase();
        if (!sku) return;
        const existing = map.get(sku) || { sku, totalDemand: 0, orders: [], imageUrl: line.imageUrl || null };
        existing.totalDemand += toNumber(line.qty);
        if (!existing.imageUrl && line.imageUrl) existing.imageUrl = line.imageUrl;
        existing.orders.push({
          id: `${card.id}_${sku}`,
          orderDocId: card.id,
          orderNumber: card.orderNumber,
          orderStatus: card.orderStatus,
          quantity: line.qty,
          qtyNumber: toNumber(line.qty),
          title: line.title,
          imageUrl: line.imageUrl,
          sku,
          isV9: true,
          allocationMode: String(line?.allocationMode || '').trim().toUpperCase(),
          componentSummary: Array.isArray(line?.componentSummary) ? line.componentSummary : [],
          stockSkuOptions: Array.isArray(line?.stockSkuOptions) ? line.stockSkuOptions : [],
          orderFullInStock: true, // all lines came from action queue — already eligible
        });
        map.set(sku, existing);
      });
    });
    return map;
  }, [inStockV9Cards]);

  const activeReadyOrders = readyV9Orders;

  const readyItemCountOptions = useMemo(() => {
    const counts = new Set();
    activeReadyOrders.forEach((o) => {
      const n = Number(o?.lineCount || 0);
      if (Number.isFinite(n) && n > 0) counts.add(n);
    });
    return Array.from(counts).sort((a, b) => a - b);
  }, [activeReadyOrders]);

  const readyBatchOptions = useMemo(() => {
    const ids = new Set();
    let hasNoBatch = false;
    activeReadyOrders.forEach((o) => {
      (o?.lines || []).forEach((line) => {
        const bids = Array.isArray(line?.batchIds) ? line.batchIds : (line?.batchId ? [line.batchId] : []);
        const activeBids = bids.map((b) => b ? String(b).trim().toUpperCase() : '').filter(Boolean);
        if (activeBids.length === 0) {
          hasNoBatch = true;
        } else {
          activeBids.forEach((bid) => ids.add(bid));
        }
      });
    });
    const sorted = Array.from(ids).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    if (hasNoBatch) {
      sorted.unshift('NO_BATCH');
    }
    return sorted;
  }, [activeReadyOrders]);

  const readyBatchCounts = useMemo(() => {
    const counts = {};
    let baseList = activeReadyOrders;
    if (readyItemCountFilter !== 'all') {
      const target = Number(readyItemCountFilter);
      if (Number.isFinite(target) && target > 0) {
        baseList = baseList.filter((o) => Number(o?.lineCount || 0) === target);
      }
    }

    baseList.forEach((order) => {
      const orderBatchIds = new Set();
      (order.lines || []).forEach((line) => {
        const bids = Array.isArray(line?.batchIds) ? line.batchIds : (line?.batchId ? [line.batchId] : []);
        const activeBids = bids.map((b) => b ? String(b).trim().toUpperCase() : '').filter(Boolean);
        if (activeBids.length === 0) {
          orderBatchIds.add('NO_BATCH');
        } else {
          activeBids.forEach((bid) => orderBatchIds.add(bid));
        }
      });

      orderBatchIds.forEach((bid) => {
        counts[bid] = (counts[bid] || 0) + 1;
      });
    });

    return counts;
  }, [activeReadyOrders, readyItemCountFilter]);

  const readyItemCountCounts = useMemo(() => {
    const counts = {};
    let baseList = activeReadyOrders;

    if (Array.isArray(readyBatchFilter) && readyBatchFilter.length > 0) {
      const selected = new Set(readyBatchFilter.map((b) => String(b).trim().toUpperCase()).filter(Boolean));
      baseList = baseList.filter((o) => {
        const orderBatchIds = new Set(
          (o?.lines || []).flatMap((line) => {
            const bids = Array.isArray(line?.batchIds) ? line.batchIds : (line?.batchId ? [line.batchId] : []);
            const activeBids = bids.map((b) => b ? String(b).trim().toUpperCase() : '').filter(Boolean);
            return activeBids.length > 0 ? activeBids : ['NO_BATCH'];
          })
        );

        for (const bid of orderBatchIds) {
          if (!selected.has(bid)) return false;
        }
        return true;
      });
    }

    baseList.forEach((order) => {
      const count = Number(order.lineCount || 0);
      if (Number.isFinite(count) && count > 0) {
        counts[count] = (counts[count] || 0) + 1;
      }
    });

    counts['all'] = baseList.length;

    return counts;
  }, [activeReadyOrders, readyBatchFilter]);

  const filteredReadyOrders = useMemo(() => {
    let list = activeReadyOrders;

    if (readyItemCountFilter !== 'all') {
      const target = Number(readyItemCountFilter);
      if (Number.isFinite(target) && target > 0) {
        list = list.filter((o) => Number(o?.lineCount || 0) === target);
      }
    }

    if (Array.isArray(readyBatchFilter) && readyBatchFilter.length > 0) {
      const selected = new Set(readyBatchFilter.map((b) => String(b).trim().toUpperCase()).filter(Boolean));
      list = list.filter((o) => {
        const orderBatchIds = new Set(
          (o?.lines || []).flatMap((line) => {
            const bids = Array.isArray(line?.batchIds) ? line.batchIds : (line?.batchId ? [line.batchId] : []);
            const activeBids = bids.map((b) => b ? String(b).trim().toUpperCase() : '').filter(Boolean);
            return activeBids.length > 0 ? activeBids : ['NO_BATCH'];
          })
        );

        for (const bid of orderBatchIds) {
          if (!selected.has(bid)) return false;
        }
        return true;
      });
    }

    return list;
  }, [activeReadyOrders, readyItemCountFilter, readyBatchFilter]);

  const sortedReadyOrders = useMemo(() => {
    const list = [...filteredReadyOrders];
    
    list.sort((a, b) => {
      let valA, valB;
      
      switch (readySortKey) {
        case 'orderNumber':
          valA = String(a.orderNumber || '').trim();
          valB = String(b.orderNumber || '').trim();
          const numA = parseInt(valA.replace(/\D/g, ''), 10);
          const numB = parseInt(valB.replace(/\D/g, ''), 10);
          if (!isNaN(numA) && !isNaN(numB)) {
            return readySortOrder === 'asc' ? numA - numB : numB - numA;
          }
          return readySortOrder === 'asc'
            ? valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
            : valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
            
        case 'sku':
          valA = a.lines?.[0]?.sku || '';
          valB = b.lines?.[0]?.sku || '';
          return readySortOrder === 'asc'
            ? valA.localeCompare(valB, undefined, { sensitivity: 'base' })
            : valB.localeCompare(valA, undefined, { sensitivity: 'base' });
            
        case 'batch':
          const getFirstBatchId = (order) => {
            for (const line of order.lines || []) {
              if (line.batchId) return line.batchId;
              if (Array.isArray(line.batchIds) && line.batchIds.length > 0) {
                const validBatch = line.batchIds.find(Boolean);
                if (validBatch) return validBatch;
              }
            }
            return '';
          };
          valA = getFirstBatchId(a);
          valB = getFirstBatchId(b);
          if (!valA && valB) return 1;
          if (valA && !valB) return -1;
          if (!valA && !valB) return 0;
          return readySortOrder === 'asc'
            ? valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
            : valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
            
        case 'itemCount':
          valA = Number(a.lineCount || 0);
          valB = Number(b.lineCount || 0);
          return readySortOrder === 'asc' ? valA - valB : valB - valA;
          
        default:
          return 0;
      }
    });
    
    return list;
  }, [filteredReadyOrders, readySortKey, readySortOrder]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(VIEW_MODE_KEY, mode);
      }
    } catch (_) {
      // Ignore storage write issues
    }
  };

  const handleViewTabChange = (tab) => {
    setViewTab(tab);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(VIEW_TAB_KEY, tab);
      }
    } catch (_) {
      // Ignore storage write issues
    }
  };

  const renderReady = () => {
    if (sortedReadyOrders.length === 0) {
      return (
        <div className="stock-empty">
          <p>No ready-to-pack lines match your filters.</p>
          <button className="stock-house-link-btn" onClick={refreshAll} disabled={isRefreshing}>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      );
    }
    return (
      <div className="ready-list">
        {sortedReadyOrders.map((item) => (
          <article key={item.id} className="ready-card">
            <div className="ready-card-body">
              <div className="ready-head">
                <div className="ready-head-left">
                  <div className="ready-order">Order #{item.orderNumber}</div>
                  <div className="ready-customer">{item.customer}</div>
                </div>
                <div className="ready-head-right">
                  <div className="ready-total-row">
                    {canPerformAction('orders.markPacked') && (
                      <button
                        type="button"
                        className="ready-pack-btn"
                        onClick={() => markPackedForV9Order(item)}
                        disabled={isMarkingPackedV9}
                        title="Mark this order as packed"
                      >
                        {isMarkingPackedV9 ? 'Marking…' : 'Mark Packed'}
                      </button>
                    )}
                    <div className="ready-total">{item.totalDisplay}</div>
                  </div>
                  <div className="ready-date">{item.orderDate}</div>
                </div>
              </div>

              <div className="ready-tags">
                {item.source && <span className="pill ghost">{item.source}</span>}
                {item.paymentType && <span className="pill ghost">{item.paymentType}</span>}
               
                <span className="ready-lines-count">{item.lineCount} Item in Order</span>
              </div>

              <div className="ready-lines-grid">
                {item.lines.map((line) => (
                  <div key={line.id} className="ready-line-card">
                    {line.imageUrl ? (
                      <img src={line.imageUrl} alt={line.sku} loading="lazy" />
                    ) : (
                      <div className="ready-line-card-img placeholder">No image</div>
                    )}
                    <div className="ready-line-card-body">
                      <div className="ready-line-main">
                        <div className="ready-line-sku">{line.sku}</div>
                        <div className="ready-line-pills">
                          {line.used && <span className="pill tiny used">Used</span>}
                        </div>
                      </div>
                      <div className="ready-line-right">
                        <div className="ready-line-qty">{formatQuantity(line.qty)} m</div>
                        <div className="ready-line-batch-bottom">
                          {line.used && Array.isArray(line.usedPieceIds) && line.usedPieceIds.length > 0 ? (
                            <span className="batch-badge stock-piece">{line.usedPieceIds.join(', ')}</span>
                          ) : Array.isArray(line.batchIds) && line.batchIds.length > 0 ? (
                            line.batchIds.includes('NO_BATCH') ? (
                              <span className="batch-badge stock">No Batch</span>
                            ) : (
                              <span className="batch-badge batch">{line.batchIds.join(', ')}</span>
                            )
                          ) : (
                            <span className="batch-badge stock">{line.batchId || 'No Batch'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  };

  return (
    <div className="stock-house-page">
      <header className="stock-house-header">
        <div className="stock-house-actions">
          {viewTab === 'inStockV9' && (
            <div className="view-toggle">
              <button
                className={viewMode === 'grid-2' ? 'active' : ''}
                onClick={() => handleViewModeChange('grid-2')}
              >
                2 col
              </button>
              <button
                className={viewMode === 'grid-1' ? 'active' : ''}
                onClick={() => handleViewModeChange('grid-1')}
              >
                1 col
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => handleViewModeChange('list')}
              >
                List
              </button>
            </div>
          )}
          {viewTab === 'readyNew' && (
            <div className="ready-filter">
              <select
                id="ready-item-count-filter"
                value={readyItemCountFilter}
                onChange={(e) => setReadyItemCountFilter(e.target.value)}
              >
                <option value="all">All ({readyItemCountCounts['all'] || 0})</option>
                {readyItemCountOptions.map((n) => {
                  const count = readyItemCountCounts[n] || 0;
                  return (
                    <option key={n} value={String(n)}>
                      {n} item{n === 1 ? '' : 's'} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          {viewTab === 'readyNew' && (
            <div className="ready-sort">
              <select
                id="ready-sort-select"
                value={`${readySortKey}-${readySortOrder}`}
                onChange={(e) => {
                  const [key, order] = e.target.value.split('-');
                  setReadySortKey(key);
                  setReadySortOrder(order);
                }}
              >
                <option value="orderNumber-desc">Order # (Newest First)</option>
                <option value="orderNumber-asc">Order # (Oldest First)</option>
                <option value="sku-asc">SKU (A-Z)</option>
                <option value="sku-desc">SKU (Z-A)</option>
                <option value="batch-asc">Batch ID (Ascending)</option>
                <option value="batch-desc">Batch ID (Descending)</option>
                <option value="itemCount-desc">Items (Most First)</option>
                <option value="itemCount-asc">Items (Fewest First)</option>
              </select>
            </div>
          )}
          {viewTab === 'readyNew' && (
            <details className="ready-batch-filter" ref={readyBatchDetailsRef}>
              <summary>
                Batches
                {readyBatchFilter.length > 0 ? (
                  <span className="ready-batch-count">{readyBatchFilter.length}</span>
                ) : null}
              </summary>
              <div className="ready-batch-filter-panel">
                <div className="ready-batch-filter-actions">
                  <button
                    type="button"
                    className="ready-batch-clear"
                    onClick={() => setReadyBatchFilter([])}
                    disabled={readyBatchFilter.length === 0}
                  >
                    Clear
                  </button>
                </div>
                <div className="ready-batch-options">
                  {readyBatchOptions.length === 0 ? (
                    <div className="ready-batch-empty">No batch ids found for current ready-to-pack list.</div>
                  ) : (
                    readyBatchOptions.map((bid) => {
                      const checked = readyBatchFilter.includes(bid);
                      const count = readyBatchCounts[bid] || 0;
                      return (
                        <label key={bid} className="ready-batch-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setReadyBatchFilter((prev) => {
                                const next = Array.isArray(prev) ? [...prev] : [];
                                const upper = String(bid).trim().toUpperCase();
                                if (e.target.checked) {
                                  if (!next.includes(upper)) next.push(upper);
                                } else {
                                  const idx = next.indexOf(upper);
                                  if (idx >= 0) next.splice(idx, 1);
                                }
                                return next;
                              });
                            }}
                          />
                          <span>{bid === 'NO_BATCH' ? 'No Batch' : bid} ({count})</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </details>
          )}
           <div className="search-field">
            <input
              type="search"
              placeholder="Search SKU or Order #"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="tab-toggle">
            <button className={viewTab === 'inStockV9' ? 'active' : ''} onClick={() => handleViewTabChange('inStockV9')}>
              In Stock
            </button>
            <button className={viewTab === 'readyNew' ? 'active' : ''} onClick={() => handleViewTabChange('readyNew')}>
              Ready to pack
            </button>
            <button className="quick-pack-nav-btn" onClick={() => navigate('/quick-pack')}>
              📱 Quick Pack
            </button>
          </div>
        </div>
      </header>

      {error && <div className="stock-error">{error}</div>}

      {/* intentionally removed shared SKUs callout */}

      {(viewTab === 'readyNew' ? isLoadingReadyV9 : isLoadingInStockV9) ? (
        <div className="stock-loading">
          {viewTab === 'readyNew' ? 'Loading ready-to-pack orders...' : 'Loading in-stock orders...'}
        </div>
      ) : viewTab === 'inStockV9' ? (
        (() => {
          const pool = Array.from(skuGroupsV9.values()).sort((a, b) =>
            String(a?.sku || '').localeCompare(String(b?.sku || ''), undefined, { numeric: true, sensitivity: 'base' })
          );
          if (pool.length === 0) {
            return (
              <div className="stock-empty">
                <p>No V9 in-stock SKUs found.</p>
                <button className="stock-house-link-btn" onClick={() => fetchInStockV9Orders()} disabled={isLoadingInStockV9}>
                  {isLoadingInStockV9 ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            );
          }
          return (
            <div className={`stock-pool-grid ${viewMode === 'list' ? 'list-view' : viewMode === 'grid-1' ? 'grid-one' : 'grid-two'}`}>
              {pool.map((group) => (
                <article key={group.sku} className="stock-pool-card">
                  <div className="pool-media">
                    {group.imageUrl ? (
                      <img src={group.imageUrl} alt={group.sku} loading="lazy" />
                    ) : (
                      <div className="image-placeholder">No image</div>
                    )}
                    {group.orders.length > 1 && <span className="status-badge conflict">Shared SKU</span>}
                  </div>
                  <div className="pool-header">
                    <div className="pool-title"><h3>{group.sku}</h3></div>
                  </div>
                  <div className="pool-orders">
                    {(() => {
                      const mergedByOrder = new Map();
                      group.orders.forEach((o) => {
                        const key = `${String(o.orderDocId || '')}::${String(o.orderNumber || '')}`;
                        const ex = mergedByOrder.get(key) || { ...o, quantity: 0, qtyNumber: 0 };
                        ex.quantity = toNumber(ex.quantity) + toNumber(o.quantity);
                        ex.qtyNumber = toNumber(ex.qtyNumber) + toNumber(o.qtyNumber ?? o.quantity);
                        mergedByOrder.set(key, ex);
                      });
                      return Array.from(mergedByOrder.values());
                    })().map((o) => (
                      <div key={o.id} className={`pool-row ${o.orderFullInStock ? 'full-in-stock' : ''}`}>
                        <span className="order-num"># {o.orderNumber}</span>
                        <span className="order-qty">{formatQuantity(o.quantity)} m</span>
                      </div>
                    ))}
                  </div>
                  <div className="piece-section">
                    <div className="piece-section-header">
                      <span className="piece-title">Available Pieces</span>
                    </div>
                    {(() => {
                      const componentOrders = group.orders.filter(
                        (o) => {
                          const cand = v9LineCandidatesByKey[getV9LineCandidateKey(o.orderDocId, o.sku)] || null;
                          return String(cand?.allocationMode || o?.allocationMode || '').trim().toUpperCase() === 'COMPONENT_STOCK';
                        }
                      );
                      if (componentOrders.length > 0) {
                        return (
                          <div className="stockhouse-component-list">
                            {componentOrders.map((order) => {
                              const cand = v9LineCandidatesByKey[getV9LineCandidateKey(order.orderDocId, order.sku)] || null;
                              const componentRows = Array.isArray(cand?.componentCandidates) && cand.componentCandidates.length > 0
                                ? cand.componentCandidates
                                : (Array.isArray(cand?.componentSummary) && cand.componentSummary.length > 0
                                  ? cand.componentSummary
                                  : (Array.isArray(order.componentSummary) ? order.componentSummary : []));
                              return (
                                <div key={`${order.orderDocId}_${order.sku}_${order.id}`} className="stockhouse-component-card">
                                  <div className="stockhouse-component-main">
                                    <div>
                                      <strong>Order #{order.orderNumber}</strong>
                                      <div className="stockhouse-component-sub">
                                        Bundle not in stock. Use component stock for {formatQuantity(order.quantity)} bundle.
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="stockhouse-component-btn"
                                      onClick={() => openV9MarkUsedModal(order)}
                                      disabled={!canMarkUsedAction || cand?.canMarkUsed === false}
                                    >
                                      Mark Used
                                    </button>
                                  </div>
                                  {componentRows.length > 0 && (
                                    <div className="stockhouse-component-grid">
                                      {componentRows.map((component) => {
                                        const piecesCount = Array.isArray(component?.pieces) ? component.pieces.length : 0;
                                        return (
                                          <div
                                            key={`${order.orderDocId}_${component.componentSku || component.sku}`}
                                            className="stockhouse-component-chip"
                                          >
                                            <span>{component.componentSku || component.sku}</span>
                                            <span>
                                              Req {formatQuantity(component.requiredQty)}m · Avl {formatQuantity(component.availableQty)}m
                                              {piecesCount > 0 ? ` · ${piecesCount} piece${piecesCount === 1 ? '' : 's'}` : ''}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      return (
                        <div className="piece-list">
                          {(() => {
                            const pieceMap = new Map();
                            let hasLoadedCandidate = false;
                            group.orders.forEach((order) => {
                              const cand = v9LineCandidatesByKey[getV9LineCandidateKey(order.orderDocId, order.sku)] || null;
                              if (!cand) return;
                              hasLoadedCandidate = true;
                              (Array.isArray(cand?.pieces) ? cand.pieces : []).forEach((piece) => {
                                const pieceKey = String(piece?.pieceId || piece?.id || '').trim();
                                if (!pieceKey) return;
                                pieceMap.set(pieceKey, {
                                  ...piece,
                                  groupedDisplaySku: piece?.fromGroupedSku ? String(piece?.stockSku || '').trim().toUpperCase() : '',
                                });
                              });
                            });
                            if (!hasLoadedCandidate && v9LineCandidatesLoading) {
                              return <div className="piece-note">Loading eligible pieces...</div>;
                            }
                            const rawPieces = Array.from(pieceMap.values());
                            const maxDemand = Math.max(...group.orders.map((o) => toNumber(o.quantity)), 0);
                            const eligiblePieces = rawPieces.filter((p) =>
                              group.orders.some((o) => toNumber(p.length) + 1e-9 >= toNumber(o.quantity))
                            );
                            const piecesForMaxDemand = eligiblePieces.filter((p) => toNumber(p.length) + 1e-9 >= maxDemand);
                            const { recommended } = pickBestPiece(piecesForMaxDemand, maxDemand);
                            if (eligiblePieces.length === 0) return <div className="piece-note">No eligible pieces for this SKU.</div>;
                            return eligiblePieces.map((piece) => {
                              const remainder = toNumber(piece.length) - maxDemand;
                              const isRec = recommended && (piece.pieceId === recommended.pieceId || piece.id === recommended.id);
                              const recClass = isRec ? (remainder === 0 ? 'rec-healthy' : remainder >= 2 ? 'rec-full' : '') : '';
                              return (
                                <button
                                  type="button"
                                  key={piece.pieceId || piece.id}
                                  className={`piece-chip ${recClass}`}
                                  onClick={() => openUsePieceModal(group, piece)}
                                  disabled={!canMarkUsedAction}
                                  aria-label={`Use piece ${piece.pieceId || piece.id} for SKU ${group.sku}`}
                                >
                                  <div className="piece-main">
                                    <span className="piece-id">{piece.pieceId || piece.id || 'Piece'}</span>
                                    <span className="piece-length">{formatQuantity(piece.length)} m</span>
                                  </div>
                                  {piece.groupedDisplaySku && (
                                    <div className="piece-sub">Stock SKU: {piece.groupedDisplaySku}</div>
                                  )}
                                </button>
                              );
                            });
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                </article>
              ))}
            </div>
          );
        })()
      ) : (
        renderReady()
      )}

      <MarkUsedModal
        isOpen={v9MarkModalOpen}
        loading={v9MarkLoading}
        submitting={v9MarkSubmitting}
        context={{
          sku: v9MarkModalRow?.sku || '',
          orderNumber: v9MarkModalRow?.orderNumber || '',
          orderId: v9MarkModalRow?.orderDocId || '',
          pendingQty: Number(v9MarkCandidates?.pendingQty || v9MarkModalRow?.qtyNumber || v9MarkModalRow?.quantity || 0),
          orderedQty: Number(v9MarkCandidates?.pendingQty || v9MarkModalRow?.qtyNumber || v9MarkModalRow?.quantity || 0),
          receivedQty: 0,
          productName: v9MarkCandidates?.productName || v9MarkModalRow?.title || v9MarkModalRow?.sku || '',
          productImage: v9MarkCandidates?.productImageUrl || v9MarkModalRow?.imageUrl || '',
          allocationMode: String(v9MarkCandidates?.allocationMode || v9MarkModalRow?.allocationMode || '').trim().toUpperCase(),
          componentSummary: Array.isArray(v9MarkCandidates?.componentSummary) ? v9MarkCandidates.componentSummary : [],
          componentCandidates: Array.isArray(v9MarkCandidates?.componentCandidates) ? v9MarkCandidates.componentCandidates : [],
        }}
        pieces={Array.isArray(v9MarkCandidates?.pieces) ? v9MarkCandidates.pieces : []}
        onClose={closeV9MarkUsedModal}
        onSubmit={submitV9MarkUsed}
      />

      {usePieceTarget && (
        <Modal isOpen={!!usePieceTarget} onClose={closeUsePieceModal}>
          <div className="stock-use-piece-modal">
            <h2>Mark Used</h2>
            <div className="stock-use-piece-meta">
              <div className="stock-use-piece-kv">
                <span className="stock-use-piece-k">SKU</span>
                <span className="stock-use-piece-v">{usePieceTarget.sku}</span>
              </div>
              <div className="stock-use-piece-kv">
                <span className="stock-use-piece-k">Piece</span>
                <span className="stock-use-piece-v">
                  {usePieceTarget.pieceId} • {formatQuantity(usePieceTarget.length)} m
                </span>
              </div>
            </div>

            {usePieceError && <div className="stock-use-piece-error">{usePieceError}</div>}

            <div className="stock-use-piece-orders">
              {usePieceCandidates.length > 1 && (
                <div className="stock-use-piece-orders-title">Select order</div>
              )}

              {usePieceCandidates.length === 0 ? (
                <div className="stock-use-piece-empty">No eligible orders found.</div>
              ) : usePieceCandidates.length === 1 ? (
                <div className="stock-use-piece-single">
                  <div className="stock-use-piece-single-top">
                    <span className="stock-use-piece-order-num">Order #{usePieceCandidates[0].orderNumber}</span>
                    <span className="stock-use-piece-qty-pill">{formatQuantity(usePieceCandidates[0].quantity)} m</span>
                  </div>
                  {usePieceCandidates[0].title && (
                    <div className="stock-use-piece-single-title">{usePieceCandidates[0].title}</div>
                  )}
                </div>
              ) : (
                <div className="stock-use-piece-order-list" role="radiogroup" aria-label="Select order">
                  {usePieceCandidates.map((c) => {
                    const candidateKey = c.invKey || c.orderDocId || c.id || '';
                    return (
                    <label
                      key={candidateKey}
                      className={`stock-use-piece-order-row ${usePieceSelectedInvKey === candidateKey ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="use-piece-order"
                        value={candidateKey}
                        checked={usePieceSelectedInvKey === candidateKey}
                        onChange={() => setUsePieceSelectedInvKey(candidateKey)}
                        disabled={isUsingPiece}
                      />
                      <div className="stock-use-piece-order-info">
                        <div className="stock-use-piece-order-top">
                          <span className="stock-use-piece-order-num">Order #{c.orderNumber}</span>
                          <span className="stock-use-piece-qty-pill">{formatQuantity(c.quantity)} m</span>
                        </div>
                        {c.title && <div className="stock-use-piece-order-title">{c.title}</div>}
                      </div>
                    </label>
                  );})}
                </div>
              )}
            </div>

            <div className="stock-use-piece-actions">
              <button className="stock-modal-btn secondary" onClick={closeUsePieceModal} disabled={isUsingPiece}>
                Cancel
              </button>
              <button
                className="stock-modal-btn primary"
                onClick={submitUsePiece}
                disabled={
                  isUsingPiece ||
                  usePieceCandidates.length === 0 ||
                  (usePieceCandidates.length > 1 && !usePieceSelectedInvKey)
                }
              >
                {isUsingPiece ? 'Marking…' : 'Mark Used'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default StockHousePage;
